import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Contact, Radio, PenLine, Filter, AlertTriangle, ArrowUpRight,
  Zap, Copy, Check, Loader2, ShieldCheck, Flame, CalendarClock,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import api, { apiError } from "@/lib/api";
import { SENTIMENT_HEX, timeAgo, titleCase } from "@/lib/format";
import { PageHeader } from "@/components/app/PageHeader";
import { StatCard } from "@/components/app/StatCard";
import { SentimentBadge } from "@/components/app/SentimentBadge";
import { Loader } from "@/components/app/Loader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

const RR_TONES = ["Calm & factual", "Optimistic & pragmatic", "Urgent & mobilizing", "Bold & contrast"];

export default function Dashboard() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["overview"],
    queryFn: () => api.get("/dashboard/overview").then((r) => r.data),
  });
  const { data: profile } = useQuery({ queryKey: ["profile"], queryFn: () => api.get("/settings/profile").then((r) => r.data) });

  if (isLoading || !data) return <Loader label="Loading dashboard…" />;
  const daysTo = (d) => Math.ceil((new Date(d + "T00:00:00").getTime() - Date.now()) / 86400000);
  const generalDate = profile?.election_date || "2026-11-03";
  const upcoming = [["Primary", profile?.primary_date], ["Election Day", generalDate]]
    .filter(([, d]) => d && daysTo(d) >= 0)
    .map(([label, d]) => [daysTo(d), label]);
  const [daysToEday, edayLabel] = upcoming.length
    ? upcoming.sort((a, b) => a[0] - b[0])[0]
    : [daysTo(generalDate), "Election Day"];
  const s = data.stats;
  const dist = data.sentiment_distribution || {};
  const pie = ["positive", "neutral", "negative"]
    .map((k) => ({ name: k, value: dist[k] || 0 }))
    .filter((d) => d.value > 0);
  const totalPie = pie.reduce((a, b) => a + b.value, 0) || 1;

  return (
    <div>
      <PageHeader
        title={`Welcome back, ${user?.name?.split(" ")[0] || "team"}`}
        subtitle="Your war room at a glance, signals, drafts, and the people behind the numbers."
        icon={LayoutDashboard}
        actions={
          <Link to="/gotv" data-testid="eday-countdown" className="flex items-center gap-2.5 rounded-full border border-brand-navy/15 bg-brand-navy/[0.05] px-4 py-2 transition-colors hover:bg-brand-navy/10">
            <CalendarClock className="h-4 w-4 text-brand-navy" />
            <span className="text-sm font-semibold text-brand-navy">{daysToEday} days to {edayLabel}</span>
          </Link>
        }
      />

      <RapidResponse signal={data.rapid_response} />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testId="stat-voters" icon={Users} accent="navy" label="Voters modeled" value={s.voters.toLocaleString()} hint="from your uploaded file" />
        <StatCard testId="stat-contacts" icon={Contact} accent="blue" label="Opt-in contacts" value={s.contacts} hint="sendable universe" />
        <StatCard testId="stat-feed" icon={Radio} accent="red" label="New signals" value={s.feed_new} hint={`${s.feed_total} total in feed`} />
        <StatCard testId="stat-drafts" icon={PenLine} accent="amber" label="Drafts pending" value={s.drafts_pending} hint="awaiting approval" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Sentiment donut */}
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-brand-ink">Feed sentiment</h3>
          <p className="text-sm text-brand-mute">Across all listening sources</p>
          <div className="relative mt-4 h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" innerRadius={52} outerRadius={76} paddingAngle={3} stroke="none">
                  {pie.map((e) => (
                    <Cell key={e.name} fill={SENTIMENT_HEX[e.name]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-2xl font-extrabold text-brand-ink">{s.feed_total}</span>
              <span className="text-xs text-brand-mute">signals</span>
            </div>
          </div>
          <div className="mt-4 flex justify-center gap-4">
            {pie.map((e) => (
              <div key={e.name} className="flex items-center gap-1.5 text-xs">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: SENTIMENT_HEX[e.name] }} />
                <span className="capitalize text-brand-mute">{e.name}</span>
                <span className="font-semibold text-brand-ink">{Math.round((e.value / totalPie) * 100)}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent signals */}
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-brand-ink">Latest signals</h3>
            <Link to="/feed" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-red hover:underline">
              View feed <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="mt-4 divide-y divide-border">
            {data.recent_feed.map((f) => (
              <div key={f.id} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-brand-ink">{f.title}</p>
                  <p className="mt-0.5 text-xs text-brand-mute">
                    {f.author} · {f.geo} · {timeAgo(f.timestamp)}
                  </p>
                </div>
                <SentimentBadge sentiment={f.sentiment} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-brand-ink">Audience</h3>
          <div className="mt-4 space-y-3">
            <Row icon={Users} label="Personas modeled" value={s.personas} to="/personas" />
            <Row icon={Filter} label="Saved segments" value={s.segments} to="/segments" />
            <Row icon={AlertTriangle} label="Negative signals" value={s.feed_negative} to="/feed" accent />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-white p-6 shadow-sm lg:col-span-2">
          <h3 className="font-heading text-lg font-bold text-brand-ink">Recent activity</h3>
          <p className="text-sm text-brand-mute">Append-only audit trail</p>
          <div className="mt-4 space-y-2.5">
            {data.recent_activity.length === 0 && <p className="text-sm text-brand-mute">No activity yet.</p>}
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-red" />
                  <span className="font-medium text-brand-ink">{titleCase(a.action)}</span>
                  <span className="text-brand-mute">by {a.actor_email}</span>
                </div>
                <span className="text-xs text-brand-mute">{timeAgo(a.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, to, accent }) {
  return (
    <Link to={to} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-slate-50">
      <span className="flex items-center gap-2.5 text-sm font-medium text-brand-ink">
        <Icon className={`h-4 w-4 ${accent ? "text-brand-red" : "text-brand-navy"}`} /> {label}
      </span>
      <span className="font-heading text-lg font-bold text-brand-ink">{value}</span>
    </Link>
  );
}

function RapidResponse({ signal }) {
  const { hasRole } = useAuth();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tone, setTone] = useState("Calm & factual");
  const [personaId, setPersonaId] = useState("none");
  const { data: personas } = useQuery({ queryKey: ["personas"], queryFn: () => api.get("/audience/personas").then((r) => r.data) });

  if (!signal) {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-xl border border-pos-border bg-pos-bg/60 px-5 py-4 animate-fade-up">
        <ShieldCheck className="h-5 w-5 text-pos-text" />
        <p className="text-sm font-medium text-pos-text">All clear, no urgent negative signals need a rapid response right now.</p>
      </div>
    );
  }

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/content/generate", {
        content_type: "social", source: "feed", feed_item_id: signal.id,
        tone, persona_id: personaId === "none" ? undefined : personaId, provider: "anthropic",
      });
      setDraft(data.draft);
      toast.success("Rapid response drafted: review & approve");
    } catch (e) {
      toast.error(apiError(e, "Could not draft response"));
    } finally {
      setLoading(false);
    }
  };

  const body = draft?.variants?.[0]?.body || "";
  const copy = () => {
    navigator.clipboard.writeText(body);
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 1500);
  };
  const publish = async () => {
    try {
      await api.patch(`/content/drafts/${draft.id}`, { status: "published" });
      setDraft((d) => ({ ...d, status: "published" }));
      toast.success("Approved & published");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div
      data-testid="rapid-response-widget"
      className="mb-6 overflow-hidden rounded-xl border border-brand-red/30 bg-gradient-to-r from-brand-red/[0.06] to-transparent shadow-sm animate-fade-up"
    >
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-red text-white">
            <Flame className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-brand-red px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-white">Rapid response</span>
              <SentimentBadge sentiment={signal.sentiment} />
              <span className="text-xs text-brand-mute">{signal.author} · {signal.geo} · {timeAgo(signal.timestamp)} · relevance {signal.relevanceScore}</span>
            </div>
            <h3 className="mt-1.5 font-heading text-base font-bold text-brand-ink">{signal.title}</h3>
            <p className="mt-0.5 line-clamp-1 text-sm text-brand-mute">{signal.text}</p>
          </div>
        </div>
        {!draft && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="rapid-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RR_TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={personaId} onValueChange={setPersonaId}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="rapid-persona-select"><SelectValue placeholder="No persona" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No persona</SelectItem>
                {personas?.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button data-testid="rapid-response-draft-btn" onClick={generate} disabled={loading} className="bg-brand-red text-white hover:bg-brand-redhover active:scale-[0.98]">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Drafting…</> : <><Zap className="mr-2 h-4 w-4" /> Draft rapid response</>}
            </Button>
          </div>
        )}
      </div>

      {draft && (
        <div className="border-t border-brand-red/20 bg-white/70 p-5">
          <div className="rounded-lg border border-border bg-white p-4">
            <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-brand-ink">{body}</pre>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button data-testid="rapid-response-copy-btn" variant="outline" size="sm" onClick={copy}>
              {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-pos-text" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />} Copy
            </Button>
            {hasRole("admin", "manager") && draft.status !== "published" && (
              <Button data-testid="rapid-response-publish-btn" size="sm" onClick={publish} className="bg-pos-text text-white hover:opacity-90">
                <Check className="mr-1.5 h-3.5 w-3.5" /> Approve & publish
              </Button>
            )}
            {draft.status === "published" && <span className="rounded-full bg-pos-bg px-2.5 py-1 text-xs font-semibold text-pos-text">Published</span>}
            <Button asChild variant="ghost" size="sm" className="text-brand-mute">
              <Link to={`/studio?draft=${draft.id}`}>Open in Studio →</Link>
            </Button>
            <span className="ml-auto flex items-center gap-1.5 text-xs text-brand-mute"><ShieldCheck className="h-3.5 w-3.5 text-brand-blue" /> Disclaimer applied · nothing posts automatically</span>
          </div>
        </div>
      )}
    </div>
  );
}
